import React, { useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader, Tooltip } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import type { MailAlias } from '../../../types/models/MailAlias';

interface MailAliasDeleteModalProps {
  alias: MailAlias | null;
  onClose: () => void;
  mailDomainId: number;
  domainName: string;
  onSuccess: () => void;
}

const DESTINATION_DISPLAY_LIMIT = 80;

function MailAliasDeleteModal({ alias, onClose, mailDomainId, domainName, onSuccess }: MailAliasDeleteModalProps) {
  const apiFetch = useApiFetch();
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetAndClose = () => {
    setError(null);
    setNotFound(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!alias) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const params = new URLSearchParams({ mail_domain_id: String(mailDomainId) });
      const response = await apiFetch(`/emails/aliases/${alias.id}?${params.toString()}`, {
        method: 'DELETE',
      });

      if (response.status === 204) {
        onSuccess();
        resetAndClose();
        return;
      }

      const body = (await response.json().catch(() => null)) as { message?: string } | null;

      if (response.status === 404) {
        setNotFound(true);
        setError(body?.message ?? 'This alias no longer exists.');
      } else {
        setError(body?.message ?? `HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete alias');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayedSource = alias?.source === '@' ? `@${domainName}` : `${alias?.source}@${domainName}`;
  const destination = alias?.destination ?? '';
  const destinationIsLong = destination.length > DESTINATION_DISPLAY_LIMIT;
  const truncatedDestination = destinationIsLong ? `${destination.slice(0, DESTINATION_DISPLAY_LIMIT)}…` : destination;

  return (
    <Modal variant="small" isOpen={alias !== null} onClose={resetAndClose} aria-label="Delete alias">
      <ModalHeader title="Delete alias" />
      <ModalBody>
        {notFound ? (
          <Alert variant="warning" isInline title="Alias no longer exists" className="pf-v6-u-mb-md">
            {error ?? 'This alias was already deleted. Close this dialog and refresh the list.'}
          </Alert>
        ) : (
          error && (
            <Alert variant="danger" isInline title="Delete failed" className="pf-v6-u-mb-md">
              {error}
            </Alert>
          )
        )}
        <Content>
          Delete the alias <strong>{displayedSource}</strong>
          {' → '}
          {destinationIsLong ? (
            <Tooltip content={destination}>
              <strong>{truncatedDestination}</strong>
            </Tooltip>
          ) : (
            <strong>{destination}</strong>
          )}
          ? Mail addressed to this alias will no longer be delivered. This action is reversible by recreating the alias
          with the same source and destination.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={handleDelete} isLoading={isSubmitting} isDisabled={isSubmitting || notFound}>
          Delete
        </Button>
        <Button variant="link" onClick={resetAndClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default MailAliasDeleteModal;
